import React from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { ExpandLessRounded, ExpandMoreRounded } from '@mui/icons-material';
import { useSafariScrollFix } from 'shared/hooks';
import { ArchivedRowChip } from 'shared/components/ArchiveSwitch';
import StyledTableRow from './StyledTableRow';

export function getDefaultRowClassName() {
  return 'table-row';
}

/**
 * @param {Object} props
 * @param {boolean} [props.clickable=false]
 * @param {string} [props.columnOrderBy='']
 * @param {string} [props.currentOrderBy='']
 * @param {string} props.name
 * @param {Function} [props.onClick]
 * @param {string} [props.order=''] - One of: '', 'desc', 'asc'
 * @param {string} [props.cellStyling='']
 */
function MUIColumnName(props) {
  const {
    clickable = false,
    columnOrderBy = '',
    currentOrderBy = '',
    name,
    order = '',
    onClick,
    cellStyling = '',
  } = props;

  if (!clickable) return <TableCell className={`row-font ${cellStyling}`}>{name}</TableCell>;

  if (!columnOrderBy || columnOrderBy !== currentOrderBy) {
    return (
      <TableCell
        key={name}
        className={`row-font clickable-column ${cellStyling}`}
        onClick={onClick}
      >
        {name}
      </TableCell>
    );
  }

  if (order === 'desc') {
    return (
      <TableCell
        key={name}
        className={`row-font clickable-column ${cellStyling}`}
        onClick={onClick}
      >
        {name}
        <ExpandMoreRounded />
      </TableCell>
    );
  }

  return (
    <TableCell
      key={name}
      className={`row-font clickable-column ${cellStyling}`}
      onClick={onClick}
    >
      {name}
      <ExpandLessRounded />
    </TableCell>
  );
}

/**
 * @param {Object} props
 * @param {Array<Object>} props.columns
 * @param {Function} [props.columns[].formatter]
 * @param {string} [props.columns[].jsonKey]
 * @param {string} props.columns[].name
 * @param {*} [props.columns[].onClick]
 * @param {string} [props.columns[].orderBy]
 * @param {Object} [props.columns[].columnStyling]
 * @param {string} [props.currentOrder]
 * @param {string} [props.currentOrderBy]
 * @param {Function} [props.getRowClassName=getDefaultRowClassName]
 * @param {string} props.noRowsText
 * @param {*} [props.onReorder]
 * @param {string} props.rowId
 * @param {Array<*>} [props.rows=[]]
 * @param {string} [props.search='']
 * @param {string} [props.className='default-table-height']
 * @param {string} [props.cellStyling='']
 * @param {boolean} [props.isLoading=false]
 */
// Renders a table according to columns and rows.
function StickyHeadTable(props) {
  const {
    columns,
    currentOrder,
    currentOrderBy,
    getRowClassName = getDefaultRowClassName,
    noRowsText,
    onReorder,
    rowId,
    rows = [],
    search = '',
    className = 'default-table-height',
    cellStyling = '',
    isLoading = false,
  } = props;

  const hasRows = !!rows && rows.length > 0;
  // Restores Safari's wheel scrolling on an early-mounted container (no-op elsewhere). The
  // frame mounts during loading, so the heal re-arms once rows arrive.
  const scrollRef = useSafariScrollFix(undefined, hasRows);

  // An empty `noRowsText` means a result list that vanishes when empty (tenant creator,
  // receiver search).
  if (!hasRows && !isLoading && !noRowsText) return null;

  // Loading and empty states keep the frame, so the page doesn't jump when rows arrive.
  return (
    <TableContainer
      ref={scrollRef}
      component={Paper}
      className={className}
    >
      <Table stickyHeader aria-label="sticky table">
        <TableHead>
          <TableRow>
            {columns.map(({
              orderBy,
              name,
              onClick,
              jsonKey,
            }) => (
              <MUIColumnName
                clickable={!!onClick || !!orderBy}
                columnOrderBy={orderBy}
                currentOrderBy={currentOrderBy}
                key={name.length > 0 ? name : jsonKey}
                name={name}
                onClick={() => {
                  if (onClick) {
                    onClick(rows || []);
                  } else if (onReorder) {
                    onReorder(orderBy);
                  }
                }}
                order={currentOrder}
                cellStyling={cellStyling}
              />
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {!hasRows && (
          <TableRow>
            <TableCell
              colSpan={columns.length}
              sx={{
                textAlign: 'center', py: 4, color: 'text.secondary', fontSize: '1rem',
              }}
            >
              {isLoading ? 'Loading\u2026' : noRowsText}
            </TableCell>
          </TableRow>
          )}
          {hasRows && rows.map((row, index, allRows) => {
            const rowClassName = getRowClassName(row);
            const archived = rowClassName.split(' ').includes('archived');
            return (
              <StyledTableRow className={rowClassName} key={row[rowId]}>
                {columns.map(({
                  jsonKey,
                  formatter,
                  name,
                  columnStyling,
                }, columnIndex) => (
                  <TableCell
                    sx={!!columnStyling && Object.keys(columnStyling)?.length > 0
                      ? columnStyling : {
                        fontSize: '1rem !important',
                        wordWrap: 'break-word',
                      }}
                    className={cellStyling}
                    key={name.length > 0 ? `${row[rowId]}-${name}${index}` : `${row[rowId]}-${jsonKey}${index}`}
                  >
                    <div className="line-clamp">
                      {formatter ? formatter(row, search, index, allRows) : row[jsonKey]}
                    </div>
                    {/* Outside the clamp, so a long name cannot clip it. */}
                    {archived && columnIndex === 0 && <ArchivedRowChip />}
                  </TableCell>
                ))}
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default React.memo(StickyHeadTable);
